import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import SequenceService from "@/services/sequenceService";
import InboxService from "@/services/inboxService";

const sameSequenceId = (left, right) => left != null && right != null && String(left) === String(right);
const requestError = (action, fallback) => action.payload || action.error?.message || fallback;

export const fetchSequences = createAsyncThunk("sequences/fetchList", async (params = {}, thunkAPI) => {
  try {
    return await SequenceService.list(params);
  } catch (e) {
    return thunkAPI.rejectWithValue(e?.response?.data || "Couldn't load sequences. Please try again.");
  }
});

export const fetchSequenceDetail = createAsyncThunk("sequences/fetchDetail", async (id, thunkAPI) => {
  try {
    return await SequenceService.get(id);
  } catch (e) {
    return thunkAPI.rejectWithValue(e?.response?.data || "Couldn't load this sequence. Please try again.");
  }
});

export const fetchInbox = createAsyncThunk("sequences/fetchInbox", async (params = {}, thunkAPI) => {
  try {
    return await InboxService.list(params);
  } catch (e) {
    return thunkAPI.rejectWithValue(e?.response?.data || "Couldn't load replies. Please try again.");
  }
});

const initialState = {
  // Sequences list
  list: { items: [], total: 0, page: 1, pageSize: 20, pages: 1 },
  listLoading: false,
  listError: null,
  listRequestId: null,
  // Detail
  current: null,
  currentLoading: false,
  currentError: null,
  currentRequestId: null,
  currentRequestedId: null,
  // Inbox
  inbox: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 50,
    pages: 1,
    counts_by_sentiment: {},
    counts_by_handled: { handled: 0, unhandled: 0 },
  },
  inboxLoading: false,
  inboxError: null,
  inboxRequestId: null,
  inboxFilters: { sentiment: "", sequence_id: "", handled: null },
};

const slice = createSlice({
  name: "sequences",
  initialState,
  reducers: {
    setInboxFilter: (state, action) => {
      const { key, value } = action.payload;
      state.inboxFilters[key] = value;
    },
    resetInboxFilters: (state) => {
      state.inboxFilters = { ...initialState.inboxFilters };
    },
    upsertSequence: (state, action) => {
      const updated = action.payload;
      if (state.current && state.current.id === updated.id) state.current = updated;
      const idx = state.list.items.findIndex((s) => s.id === updated.id);
      if (idx >= 0) state.list.items[idx] = updated;
    },
    markReplyHandled: (state, action) => {
      const id = action.payload;
      const item = state.inbox.items.find((r) => r.id === id);
      if (item && !item.handled) {
        item.handled = true;
        const counts = state.inbox.counts_by_handled || { handled: 0, unhandled: 0 };
        counts.unhandled = Math.max(0, (counts.unhandled || 0) - 1);
        counts.handled = (counts.handled || 0) + 1;
        state.inbox.counts_by_handled = counts;
      }
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchSequences.pending, (s, a) => {
      s.listLoading = true;
      s.listError = null;
      s.listRequestId = a.meta.requestId;
    })
      .addCase(fetchSequences.fulfilled, (s, a) => {
        if (s.listRequestId !== a.meta.requestId) return;
        s.listLoading = false;
        s.listRequestId = null;
        s.list = a.payload;
      })
      .addCase(fetchSequences.rejected, (s, a) => {
        if (s.listRequestId !== a.meta.requestId) return;
        s.listLoading = false;
        s.listRequestId = null;
        s.listError = requestError(a, "Couldn't load sequences. Please try again.");
      });

    b.addCase(fetchSequenceDetail.pending, (s, a) => {
      s.currentLoading = true;
      s.currentError = null;
      s.currentRequestId = a.meta.requestId;
      s.currentRequestedId = a.meta.arg;
      // Keep a same-sequence refresh visible, but never retain a previous route's detail.
      if (!sameSequenceId(s.current?.id, a.meta.arg)) s.current = null;
    })
      .addCase(fetchSequenceDetail.fulfilled, (s, a) => {
        if (s.currentRequestId !== a.meta.requestId) return;
        s.currentLoading = false;
        s.currentRequestId = null;
        if (sameSequenceId(a.payload?.id, a.meta.arg)) {
          s.current = a.payload;
        } else {
          s.current = null;
          s.currentError = "The server returned a different sequence. Please retry.";
        }
      })
      .addCase(fetchSequenceDetail.rejected, (s, a) => {
        if (s.currentRequestId !== a.meta.requestId) return;
        s.currentLoading = false;
        s.currentRequestId = null;
        s.currentError = requestError(a, "Couldn't load this sequence. Please try again.");
      });

    b.addCase(fetchInbox.pending, (s, a) => {
      s.inboxLoading = true;
      s.inboxError = null;
      s.inboxRequestId = a.meta.requestId;
    })
      .addCase(fetchInbox.fulfilled, (s, a) => {
        if (s.inboxRequestId !== a.meta.requestId) return;
        s.inboxLoading = false;
        s.inboxRequestId = null;
        s.inbox = a.payload;
      })
      .addCase(fetchInbox.rejected, (s, a) => {
        if (s.inboxRequestId !== a.meta.requestId) return;
        s.inboxLoading = false;
        s.inboxRequestId = null;
        s.inboxError = requestError(a, "Couldn't load replies. Please try again.");
      });
  },
});

export const { setInboxFilter, resetInboxFilters, upsertSequence, markReplyHandled } = slice.actions;
export default slice.reducer;
